class RepenicCartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;

    this.lifecycle = new AbortController();
    this.timer = null;
    this.request = null;
    this.version = 0;

    const schedule = () => {
      window.clearTimeout(this.timer);

      // Hide old recommendations while the cart is changing.
      this.hidden = true;
      this.version += 1;
      this.request?.abort();

      this.timer = window.setTimeout(() => {
        this.load();
      }, 350);
    };

    for (const name of ['cart:add', 'cart:update']) {
      document.addEventListener(name, schedule, {
        signal: this.lifecycle.signal,
      });
    }

    // Refresh when the drawer opens, including after page navigation.
    const dialog = this.closest('dialog');

    if (dialog) {
      this.observer = new MutationObserver(() => {
        if (dialog.open) schedule();
      });

      this.observer.observe(dialog, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    this.load();
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.request?.abort();
    this.observer?.disconnect();
    window.clearTimeout(this.timer);
    this.version += 1;
    this.lifecycle = null;
  }

  async load() {
    if (!this.isConnected) return;

    const version = ++this.version;

    this.request?.abort();
    const request = new AbortController();
    this.request = request;

    // Stop a stalled request without affecting the cart.
    const timeout = window.setTimeout(() => {
      request.abort();
    }, 10000);

    try {
      const root = new URL(
        this.dataset.root || '/',
        window.location.origin
      );

      if (!root.pathname.endsWith('/')) {
        root.pathname += '/';
      }

      const cartResponse = await fetch(new URL('cart.js', root), {
        signal: request.signal,
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!cartResponse.ok) throw new Error('Cart unavailable');

      const cart = await cartResponse.json();
      const items = cart.items || [];

      if (!items.length) {
        if (version === this.version) this.hidden = true;
        return;
      }

      const excluded = new Set(
        items.map((item) => String(item.product_id))
      );

      // Use the first cart item as the recommendation source.
      const url = new URL('recommendations/products.json', root);

      url.searchParams.set('product_id', items[0].product_id);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');

      const response = await fetch(url, {
        signal: request.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Recommendations unavailable');

      const data = await response.json();
      const seen = new Set();

      const products = (data.products || []).filter((product) => {
        const id = String(product.id);

        if (
          !product.available ||
          excluded.has(id) ||
          seen.has(id) ||
          !product.url ||
          !product.featured_image
        ) {
          return false;
        }

        seen.add(id);
        return true;
      }).slice(0, 2);

      if (!this.isConnected || version !== this.version) return;

      const grid = this.querySelector('[data-recommendation-grid]');
      if (!grid) return;

      const fragment = document.createDocumentFragment();

      for (const product of products) {
        fragment.append(this.createCard(product, cart.currency));
      }

      grid.replaceChildren(fragment);
      this.hidden = products.length === 0;
    } catch (error) {
      if (this.isConnected && version === this.version) {
        this.hidden = true;
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }

  createCard(product, currency) {
    const link = document.createElement('a');
    link.className = 'rc-recommendations__card';

    // Preserve Shopify's recommendation tracking URL.
    const productUrl = new URL(product.url, window.location.origin);

    if (productUrl.origin !== window.location.origin) {
      throw new Error('Invalid product URL');
    }

    link.href = productUrl.href;

    const media = document.createElement('span');
    media.className = 'rc-recommendations__media';

    const image = document.createElement('img');
    image.className = 'rc-recommendations__image';
    image.src = product.featured_image;
    image.alt = product.title;
    image.width = 300;
    image.height = 300;
    image.loading = 'lazy';
    image.decoding = 'async';
    media.append(image);

    const title = document.createElement('span');
    title.className = 'rc-recommendations__title';
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'rc-recommendations__price';

    const formatted = new Intl.NumberFormat(
      document.documentElement.lang || 'en-GB',
      {
        style: 'currency',
        currency: currency || 'GBP',
      }
    ).format(Number(product.price) / 100);

    price.textContent = product.price_varies
      ? `From ${formatted}`
      : formatted;

    const action = document.createElement('span');
    action.className = 'rc-recommendations__action';
    action.textContent = 'View options';

    link.append(media, title, price, action);
    return link;
  }
}

if (!customElements.get('repenic-cart-recommendations')) {
  customElements.define(
    'repenic-cart-recommendations',
    RepenicCartRecommendations
  );
}